/* eslint-disable import/no-named-as-default */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Creatable from 'react-select/creatable';
import Select, { CSSObjectWithLabel } from 'react-select';

import styles from './bundle-manager.module.scss';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import {
  Checkbox,
  CheckboxChange,
} from '@/components/common/CheckboxTag4/CheckboxTag4';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import useGetBundles from '../hooks/useGetBundles';
import useCreateOrAssignBundle from '../hooks/useCreateOrAssignBundle';
import useUnbundle from '../hooks/useUnbundle';
import { useSelectedLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { useSelectedVariableLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import {
  BundleOutput,
  BundleFilterInput,
  BundleTypeEnum,
} from '@/graphql/contracts/_generated_/contracts';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { softwareCompanies } from '@/utils/data-helpers/softwareCompanies';

const bundleTypes = [
  { value: 'Contractual', label: 'Contractual' },
  { value: 'Custom', label: 'Custom' },
  { value: 'Suite', label: 'Suite' },
];

interface IBundleManagerProps {
  contractId: string;
  onClose: () => void;
}

interface IBundleFormValues {
  bundleId: string | undefined;
  description: string | undefined;
  hideInvoiceLines: boolean;
  hideInvoiceValues: boolean;
  softwareCompany: string | undefined;
  type: BundleTypeEnum | undefined;
}

interface IContractLineRow {
  id: string;
  [key: string]: any;
}

const validationSchema = Yup.object().shape({
  description: Yup.string()
    .optional()
    .max(240, 'Description must be max 240 characters')
    .required('Description is required'),
  softwareCompany: Yup.string()
    .optional()
    .max(32, 'softwareCompany must be max 32 characters'),
  hideInvoiceLines: Yup.boolean().optional(),
  hideInvoiceValues: Yup.boolean().optional(),
  bundleId: Yup.string().nullable(),
});


export default function BundleManager({
  contractId,
  onClose,
}: IBundleManagerProps) {
  const { getBundles, isLoadingGetBundles } = useGetBundles();
  const { createOrAssignBundle } = useCreateOrAssignBundle(contractId);
  const { unbundle } = useUnbundle(contractId);

  const [bundles, setBundles] = useState<BundleOutput[]>([]);
  const [isNew, setIsNew] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentAction, setCurrentAction] = useState<'Create or Assign to Bundle' | 'Unbundle'>();

  const selectedLineRows = useSelectedLines() as IContractLineRow[];
  const selectedVariableLineRows =
    useSelectedVariableLines() as IContractLineRow[];

  const selectedLinesHaveBundles = useMemo(() => {
    return (
      selectedLineRows.some((row) => row.payload.contractLine.bundleDescription) ||
      selectedVariableLineRows.some((row) => row.payload.contractLine.bundleDescription)
    );
  }, [selectedLineRows, selectedVariableLineRows]);

  useEffect(() => {
    const fetchBundles = async () => {
      const filter: BundleFilterInput = {
        contractId: { eq: contractId },
      };

      const result = await getBundles({
        filter,
        first: 1000000,
        sort: [
          {
            field: 'Description',
            order: 'Ascending',
          },
        ]
      });

      if (result?.items) {
        setBundles(result.items as BundleOutput[]);
      }
    };

    fetchBundles();
  }, []);

  const handleUnbundle = async () => {
    const contractLineIds = [
      ...selectedLineRows.map((row: IContractLineRow) => row.id),
      ...selectedVariableLineRows.map((row: IContractLineRow) => row.id),
    ];

    if (contractLineIds.length === 0) {
      return;
    }

    await unbundle({ contractId, contractLineIds });
  };

  const getSelectedContractLineIds = () => {
    return [
      ...selectedLineRows.map((row: IContractLineRow) => row.id),
      ...selectedVariableLineRows.map((row: IContractLineRow) => row.id),
    ];
  };

  const handleCreateOrAssign = async (values: IBundleFormValues) => {
    const contractLineIds = getSelectedContractLineIds();

    if (contractLineIds.length === 0) {
      return;
    }

    await createOrAssignBundle({
      bundleId: values.bundleId,
      contractId,
      contractLineIds,
      hideInvoiceLines: values.bundleId ? undefined : values.hideInvoiceLines,
      hideInvoiceValues: values.bundleId ? undefined : values.hideInvoiceValues,
      softwareCompany: values.bundleId
        ? undefined
        : (values.softwareCompany === undefined ? null : values.softwareCompany),
      type: values.bundleId ? undefined : values.type,
      description: values.bundleId ? undefined : values.description,
    });
  };

  const handleSubmit = (action: 'Create or Assign to Bundle' | 'Unbundle') => {
    setCurrentAction(action);
    setShowConfirmationModal(true);
  };

  const handleConfirmSubmit = async (values: IBundleFormValues) => {

    if (currentAction === 'Create or Assign to Bundle') {
      await handleCreateOrAssign(values);
    } else {
      await handleUnbundle();
    }
    setShowConfirmationModal(false);
    onClose();
  };

  const customFontStyle = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
    ...base,
    fontSize: '13px',
  } as CSSObjectWithLabel);

  return (
    <Formik<IBundleFormValues>
      initialValues={{
        bundleId: undefined,
        description: '',
        hideInvoiceLines: false,
        hideInvoiceValues: false,
        softwareCompany: undefined,
        type: undefined,
      }}
      validationSchema={validationSchema}
      onSubmit={() => handleSubmit('Create or Assign to Bundle')}
      validateOnMount={true}
    >
      {({ values, setFieldValue, isValid, errors, touched, setValues }) => (
        <Form>

          <AgConfirmationModal
            isOpen={showConfirmationModal}
            title={`Confirm ${currentAction ?? 'Action'}`}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={async () => handleConfirmSubmit(values)}
            contentStyle={{ width: 600 }}
          >
            This action will remove all empty bundles from the contract. Are you sure you want to continue?
          </AgConfirmationModal>

          <div className="mb-4 bundle-container">
            <TopLabel id="bundleSelect" label="Select Bundle" required={true} withMargin={true} />

            <Creatable
              classNames={styles['bundle-select']}
              styles={{
                control: (base) => customFontStyle(base),
                placeholder: (base) => customFontStyle(base),
                singleValue: (base) => customFontStyle(base),
                menu: (base) => customFontStyle(base)
              }}
              id="bundleSelect"
              options={bundles.map((bundle) => ({
                value: bundle.id,
                label: bundle.description,
                bundle,
              }))}
              onChange={(option: any) => {
                if (option) {
                  if (option.__isNew__) {
                    setIsNew(true);
                    setValues({
                      bundleId: undefined,
                      description: option.label,
                      hideInvoiceLines: false,
                      hideInvoiceValues: false,
                      softwareCompany: undefined,
                      type: undefined,
                    });
                  } else {
                    setIsNew(false);
                    const { bundle } = option;
                    setValues({
                      bundleId: bundle.id,
                      description: bundle.description,
                      hideInvoiceLines: bundle.hideInvoiceLines,
                      hideInvoiceValues: bundle.hideInvoiceValues,
                      softwareCompany: bundle.softwareCompany ?? undefined,
                      type: bundle.type,
                    });
                  }
                } else {
                  setIsNew(false);
                  setValues({
                    bundleId: undefined,
                    description: '',
                    hideInvoiceLines: false,
                    hideInvoiceValues: false,
                    softwareCompany: undefined,
                    type: undefined,
                  });
                }
              }}
              placeholder="Select or create a bundle..."
              isLoading={isLoadingGetBundles}
              isClearable
              formatCreateLabel={(inputValue) =>
                `Create new bundle '${inputValue}'`
              }
              className="mb-2"
            />
          </div>

          <div className="d-flex" >
            <div className="col-6 mb-3">
              <Checkbox
                id="hideInvoiceLines"
                checked={values.hideInvoiceLines}
                onChange={(e: CheckboxChange) =>
                  setFieldValue('hideInvoiceLines', e.checked)
                }
                disabled={!isNew}
              >
                Hide Invoice Lines
              </Checkbox>
            </div>
            <div className="col-6 mb-3">
              <Checkbox
                title='Hide Invoice Values'
                id="hideInvoiceValues"
                checked={values.hideInvoiceValues}
                onChange={(e: CheckboxChange) =>
                  setFieldValue('hideInvoiceValues', e.checked)
                }
                disabled={!isNew}
              >
                Hide Invoice Values
              </Checkbox>
            </div>
          </div>

          <div className="mb-3">
            <TopLabel id="softwareCompany" label="Software Company" required={false} withMargin={true} />
            <div >
              <Select
                id="softwareCompany"
                options={softwareCompanies}
                value={softwareCompanies.find(option => option.value === values.softwareCompany) || null}
                onChange={(selectedOption) => {
                  setFieldValue('softwareCompany', selectedOption ? selectedOption.value : undefined);
                }}
                placeholder="Select software company..."
                isDisabled={!isNew}
                isClearable
                maxMenuHeight={120}
                styles={{
                  control: (base) => customFontStyle(base),
                  placeholder: (base) => customFontStyle(base),
                  singleValue: (base) => customFontStyle(base),
                  menu: (base) => customFontStyle(base)
                }}
              />
            </div>
            {errors.softwareCompany && touched.softwareCompany && (
              <ValidationAlert isFloating={true} type="danger">
                {errors.softwareCompany}
              </ValidationAlert>
            )}
          </div>

          <div className="mb-3">
            <TopLabel id="type" label="Type" required={false} withMargin={true} />
            <div >
              <Select
                id="type"
                options={bundleTypes}
                value={bundleTypes.find(option => option.value === values.type) || null}
                onChange={(selectedOption) => {
                  setFieldValue('type', selectedOption?.value);
                }}
                placeholder="Select bundle type..."
                isDisabled={!isNew}
                isClearable
                maxMenuHeight={120}
                styles={{
                  control: (base) => customFontStyle(base),
                  placeholder: (base) => customFontStyle(base),
                  singleValue: (base) => customFontStyle(base),
                  menu: (base) => customFontStyle(base)
                }}
              />
            </div>
            {errors.type && touched.type && (
              <ValidationAlert isFloating={true} type="danger">
                {errors.type}
              </ValidationAlert>
            )}
          </div>

          <div className={styles['assign-button-container']}>
            <Button
              buttonStyleType={ButtonStyleType.PRIMARY}
              type="submit"
              disabled={!isValid || !getSelectedContractLineIds().length}
            >
              {isNew ? 'Create & Assign' : 'Assign to Bundle'}
            </Button>
          </div>
          <ActionButtonsWrapper
            leftButton={
              <Button
                buttonStyleType={ButtonStyleType.PRIMARY}
                onClick={onClose}
              >
                Cancel
              </Button>
            }
            rightButton={
              <Button
                buttonStyleType={ButtonStyleType.WARNING}
                className={styles['unbundle-button']}
                onClick={() => handleSubmit('Unbundle')}
                disabled={!selectedLinesHaveBundles || !getSelectedContractLineIds().length}
              >
                Unbundle
              </Button>
            }
          />
        </Form>
      )}
    </Formik>
  );
}
